import { FixedBanner } from '@circlefin/components'
import { usePermission } from '@circlefin/permissions/react'
import { useCurrentCurrency } from '@features/locales/hooks/currency'
import { CIRCLE_WALLETS_QUERY } from '@services/permissions'
import { Currency, useActiveWalletApprovalPolicyQuery } from '@shared/graphql'
import { useConnectWallet } from '@shared/wallet/hooks/useConnectWallet'
import useTranslation from 'next-translate/useTranslation'

import { ConnectRecipientWallet } from '../ConnectRecipientWallet/ConnectRecipientWallet'

import type { OnChainTransferDestination } from '@modals/blockchain'

export interface WalletConnectSectionProps {
  /**
   * Destination wallet.
   */
  destination: OnChainTransferDestination
  /**
   * On Success Callback.
   */
  onSuccess: () => void
  /**
   * On Error Callback.
   */
  onError: () => void
  /**
   * Custom className.
   */
  className?: string
}

export const WalletConnectSection: React.FC<WalletConnectSectionProps> = ({
  destination,
  onSuccess,
  onError,
  className,
}) => {
  const { t } = useTranslation('ace')
  const [, { isAuthorized }] = usePermission(CIRCLE_WALLETS_QUERY)
  const [{ currency }] = useCurrentCurrency()
  const { isConnected } = useConnectWallet()

  const walletPolicyQuery = useActiveWalletApprovalPolicyQuery({
    variables: { currency: currency ?? Currency.USDC },
    skip: !isAuthorized,
    fetchPolicy: 'cache-and-network',
  })
  const hasWalletPolicyWithApproverSteps =
    walletPolicyQuery.data?.activeWalletApprovalPolicy?.approvalWorkflow != null

  return (
    <div className={className}>
      {!isConnected ? (
        <div className="mt-6 border-t border-black-50">
          <p className="pt-4">
            {t('transfer.walletConnectSection.helperText')}
          </p>
        </div>
      ) : null}
      <div className="mt-6 border-t border-black-50">
        <ConnectRecipientWallet
          address={destination.address}
          chain={destination.chain}
          label={t('transfer.walletConnectSection.connectedWalletLabel')}
          onError={onError}
          onSuccess={onSuccess}
        />
      </div>
      {/* ACE transfer alert */}
      {hasWalletPolicyWithApproverSteps ? (
        <FixedBanner className="mt-4" multiline={false} status="info">
          <FixedBanner.Description>
            {t(`alert.transfer.description`)}
          </FixedBanner.Description>
        </FixedBanner>
      ) : null}
    </div>
  )
}
